import React, { useRef, useState } from "react";
import axios from "axios";
import { Send, CheckCircle2, AlertTriangle } from "lucide-react";
import TurnstileWidget from "@/components/TurnstileWidget";
import { useLang } from "@/lib/useLang";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_MSG = 2000;
const cpLength = (s) => [...(s || "")].length;

/**
 * Public "Get in touch" form. Replaces the static mailto link on /about.
 * Submits to POST /api/contact (Turnstile-verified, IP-rate-limited).
 */
export const ContactForm = () => {
  const { t, lang } = useLang();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const turnstileRef = useRef(null);

  const msgLen = cpLength(message);
  const overLimit = msgLen > MAX_MSG;
  const tooShort = msgLen < 10;
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const disabled =
    status === "submitting" || !name.trim() || !validEmail || tooShort || overLimit;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (disabled) return;
    setStatus("submitting");
    setErrorMsg("");
    let token = null;
    try {
      token = await turnstileRef.current?.getToken();
    } catch {/* ignore */}
    try {
      await axios.post(`${API}/contact`, {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        turnstile_token: token,
      });
      setStatus("success");
      setName(""); setEmail(""); setMessage("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(
        typeof detail === "string" && detail
          ? detail
          : (lang === "ml"
              ? "സന്ദേശം അയക്കാൻ കഴിഞ്ഞില്ല. ദയവായി വീണ്ടും ശ്രമിക്കുക."
              : "Couldn't send your message. Please try again."),
      );
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div
        className="brutal-card p-5 md:p-6"
        style={{ background: "#0A0A0A", color: "#FDFBF7" }}
        data-testid="contact-success"
      >
        <p className="text-xs uppercase font-extrabold tracking-[0.3em] mb-2 flex items-center gap-2" style={{ color: "#138808" }}>
          <CheckCircle2 size={16} strokeWidth={3} />
          {lang === "ml" ? "സന്ദേശം ലഭിച്ചു" : "MESSAGE RECEIVED"}
        </p>
        <h3 className="font-display uppercase leading-none mt-1" style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)" }}>
          {lang === "ml" ? "നന്ദി. ഉടനെ മറുപടി തരാം." : "Thanks. We'll get back to you."}
        </h3>
        <p className="text-sm md:text-base font-medium mt-3 opacity-90">
          {lang === "ml"
            ? "നിങ്ങളുടെ സന്ദേശം ടീമിന് അയച്ചു. പ്രസ്സ്/സഹകരണ ചോദ്യങ്ങൾക്ക് 1-2 പ്രവൃത്തി ദിവസത്തിൽ മറുപടി പ്രതീക്ഷിക്കാം."
            : "Your message is now with the Kerala Speaks team. Press & partnership requests usually hear back within 1–2 working days."}
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="brutal-btn mt-5"
          style={{ background: "#FF9933", color: "#0A0A0A", borderColor: "#FF9933" }}
          data-testid="contact-send-another"
        >
          {lang === "ml" ? "മറ്റൊരു സന്ദേശം" : "Send another"} →
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="brutal-card p-5 md:p-6"
      style={{ background: "#0A0A0A", color: "#FDFBF7" }}
      data-testid="contact-form"
    >
      <p
        className="text-xs uppercase font-extrabold tracking-[0.3em] mb-2"
        style={{ color: "#FF9933" }}
        data-testid="contact-label"
      >
        {t("about_contact_label")}
      </p>
      <h3 className="font-display uppercase leading-none" style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)" }}>
        {lang === "ml" ? "ഞങ്ങൾക്ക് എഴുതൂ." : "Drop us a line."}
      </h3>
      <p className="text-sm md:text-base font-medium mt-2 mb-5 opacity-90 max-w-2xl">
        {t("about_contact_body")}
      </p>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <Field label={lang === "ml" ? "നിങ്ങളുടെ പേര്" : "Your name"}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            required
            data-testid="contact-name"
            className="w-full bg-transparent border-2 border-[#2a2a2a] focus:border-[#FF9933] outline-none px-3 py-2 text-sm md:text-base"
            placeholder={lang === "ml" ? "ഉദാ. നിമിഷ കെ." : "e.g. Anjali Nair"}
          />
        </Field>
        <Field label={lang === "ml" ? "ഇ-മെയിൽ" : "Email"}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={254}
            required
            data-testid="contact-email"
            className="w-full bg-transparent border-2 border-[#2a2a2a] focus:border-[#FF9933] outline-none px-3 py-2 text-sm md:text-base"
            placeholder="you@example.com"
          />
        </Field>
      </div>

      <Field label={lang === "ml" ? "സന്ദേശം" : "Message"}>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          maxLength={MAX_MSG * 2}
          required
          data-testid="contact-message"
          className="w-full bg-transparent border-2 border-[#2a2a2a] focus:border-[#FF9933] outline-none px-3 py-2 text-sm md:text-base resize-vertical"
          placeholder={lang === "ml"
            ? "എന്തിനെക്കുറിച്ചാണ് നിങ്ങൾ ഞങ്ങളെ ബന്ധപ്പെടുന്നത്?"
            : "Press, partnerships, civic-tech collaboration, or feedback…"}
        />
      </Field>

      <div className="flex items-center justify-between mt-1 mb-4 text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-400">
        <span data-testid="contact-counter">
          {msgLen} / {MAX_MSG}
        </span>
        {overLimit && <span style={{ color: "#FF9933" }}>{lang === "ml" ? "ദൈർഘ്യം കൂടി" : "Too long"}</span>}
      </div>

      {status === "error" && errorMsg && (
        <div
          className="flex items-start gap-2 border-2 p-3 mb-4 text-sm"
          style={{ background: "#2a0a0a", borderColor: "#E3000F", color: "#FFB1B1" }}
          data-testid="contact-error"
        >
          <AlertTriangle size={16} strokeWidth={3} className="shrink-0 mt-0.5" />
          <p className="font-medium leading-snug">{errorMsg}</p>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <TurnstileWidget ref={turnstileRef} />
        <button
          type="submit"
          disabled={disabled}
          className="brutal-btn shrink-0"
          style={{
            background: disabled ? "#2a2a2a" : "#FF9933",
            color: disabled ? "#737373" : "#0A0A0A",
            borderColor: disabled ? "#2a2a2a" : "#FF9933",
          }}
          data-testid="contact-submit"
        >
          <Send size={16} strokeWidth={3} className="mr-2" />
          {status === "submitting"
            ? (lang === "ml" ? "അയക്കുന്നു…" : "Sending…")
            : (lang === "ml" ? "സന്ദേശം അയക്കുക" : "Send message")}
        </button>
      </div>
    </form>
  );
};

const Field = ({ label, children }) => (
  <label className="block mb-2">
    <span className="block text-[10px] uppercase font-extrabold tracking-[0.3em] text-neutral-400 mb-1.5">{label}</span>
    {children}
  </label>
);

export default ContactForm;
